import { createAsyncThunk } from "@reduxjs/toolkit";
import { OrderAndSalesAxios } from "../../api/orderandsalesaxios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { Axios } from "api/axios";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

/* -----------------------------------------------------------
 🧮 Utility: Build Query Params
----------------------------------------------------------- */
const buildQueryParams = (
  page: number,
  pageSize: number,
  search?: string,
  sort?: string,
  order?: "asc" | "desc" | "",
  filters?: any,
  stage?: any,
  searchType?: string
) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("page_size", pageSize.toString());
  params.append("stage", stage);
  if (search) params.append("search", search);
  if (searchType) params.append("type", searchType);

  if (sort) {
    const orderingValue = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingValue);
  }

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          // Handle date range filters first (created_at, updated_at)
          if (
            filterField === "created_at" ||
            filterField === "updated_at"
          ) {
            if (typeof filterValue === "object" && filterValue !== null) {
              const from = filterValue?.from || filterValue?.from_date;
              const to = filterValue?.to || filterValue?.to_date;

              if (from) {
                params.append(`${filterField}_from`, from);
              }
              if (to) {
                params.append(`${filterField}_to`, to);
              }
              if (from || to) {
                params.append("filter_by", filterField);
              }
            }
            return; // Stop processing this filter here to avoid duplicates
          }

          // Handle generic object filters (dropdowns, etc.)
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
            });
          } else {
            // Handle simple filters
            params.append(filterField, filterValue);
          }
        }

        if (filterField === "quotation__quotation_number") {
          params.append("quotation_number", filterValue);
        }
      }
    );
  }

  return params;
};

/* -----------------------------------------------------------
 🧾 BASE URL
----------------------------------------------------------- */
const BASE_URL = `sales_order`;

/* -----------------------------------------------------------
 🧾 PRODUCT SALES ORDERS
----------------------------------------------------------- */

export const downloadSalesOrderPdf = createAsyncThunk(
  "sales_order/downloadSalesOrderPdf",
  async (
    {
      id,
      type,
      salesOrderNumber,
    }: { id: string; type: "pi" | "acknowledgement"; salesOrderNumber: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT.SALES_ORDER.PRODUCT.DOWNLOAD_PDF(id)}?type=${type}`,
        {
          responseType: "blob",
        }
      );

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filePrefix = type === "pi" ? "PI" : "Order_Acknowledgement";
      link.setAttribute("download", `${filePrefix}_${salesOrderNumber}.pdf`); // Set the file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up the URL object

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to download PDF");
    }
  }
);

/**
 * Fetch all product sales orders
 */
export const fetchAllProductSalesOrders = createAsyncThunk(
  "sales_order/fetchAllProducts",
  async (
    {
      page,
      pageSize,
      sort,
      order,
      search,
      type,
      filters,
      stage,
    }: {
      page: number;
      pageSize: number;
      sort?: string;
      order?: "asc" | "desc" | "";
      search?: string;
      type?: string;
      filters?: any[];
      stage?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (stage) params.append("stage", stage);
      if (search) params.append("search", search);
      if (type) params.append("type", type);
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
        sales_order_stage: stage,
        ...(type ? { type } : {}),
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT.SALES_ORDER.PRODUCT.LIST}?${params.toString()}`,
        body
      );
      return {
        data: {
          data: response.data.data || response.data || [],
          count: response.data.count || 0,
        },
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Create a new product sales order
 */
export const createProductSalesOrder = createAsyncThunk(
  "sales_order/createProduct",
  async (payload: any, { rejectWithValue }) => {
    try {
      // Always use FormData for consistency with update
      const attachmentFiles = payload._attachmentFiles || [];
      const pbgDocumentFiles = payload._pbgDocumentFiles || [];
      const customerPoDocumentFiles = payload._customerPoDocumentFiles || [];

      // console.log(
      //   "createProductSalesOrder - attachmentFiles:",
      //   attachmentFiles
      // );
      // console.log(
      //   "createProductSalesOrder - pbgDocumentFiles:",
      //   pbgDocumentFiles
      // );
      // console.log(
      //   "createProductSalesOrder - customerPoDocumentFiles:",
      //   customerPoDocumentFiles
      // );

      // Create FormData for file uploads
      const formData = new FormData();

      // Remove file arrays from payload before stringifying
      const {
        _attachmentFiles,
        _pbgDocumentFiles,
        _customerPoDocumentFiles,
        ...payloadWithoutFiles
      } = payload;

      // console.log(
      //   "createProductSalesOrder - payloadWithoutFiles:",
      //   payloadWithoutFiles
      // );

      // Append the JSON payload as a string (backend will parse this)
      formData.append("data", JSON.stringify(payloadWithoutFiles));

      // Append attachment files with proper field name
      attachmentFiles.forEach((file: File, index: number) => {
        // console.log(`Appending attachment file ${index}:`, file.name, file);
        formData.append(`attachments`, file, file.name);
      });

      // Append PBG document files with proper field name
      pbgDocumentFiles.forEach((file: File, index: number) => {
        // console.log(`Appending pbg_document file ${index}:`, file.name, file);
        formData.append(`pbg_documents`, file, file.name);
      });

      // Append customer PO document files with proper field name
      customerPoDocumentFiles.forEach((file: File, index: number) => {
          // console.log(
          //   `Appending customer_po_document file ${index}:`,
          //   file.name,
          //   file
          // );
        formData.append(`customer_po_documents`, file, file.name);
      });

      // Log FormData contents for debugging
      // console.log("FormData entries:");
      Array.from(formData.entries()).forEach(([key, value]) => {
        // console.log(key, value instanceof File ? `File: ${value.name}` : value);
      });

      // Axios will automatically set Content-Type to multipart/form-data
      // Don't set it manually as it needs to include the boundary
      const response = await Axios.post(
        API_ENDPOINT.SALES_ORDER.PRODUCT.CREATE,
        formData
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Retrieve single product sales order by ID
 */
export const fetchProductSalesOrderById = createAsyncThunk(
  "sales_order/fetchProductById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.SALES_ORDER.PRODUCT.DETAIL(id)
      );
      // console.log(
      //   "response in line 225 fetchProductSalesOrderById thunk",
      //   response
      // );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update product sales order
 */
export const updateProductSalesOrder = createAsyncThunk(
  "sales_order/updateProduct",
  async (
    { id, payload }: { id: string; payload: any },
    { rejectWithValue }
  ) => {
    try {
      // Always use FormData for consistency with create
      const attachmentFiles = payload._attachmentFiles || [];
      const pbgDocumentFiles = payload._pbgDocumentFiles || [];
      const customerPoDocumentFiles = payload._customerPoDocumentFiles || [];

      // Create FormData for file uploads
      const formData = new FormData();

      // Remove file arrays from payload before stringifying
      const {
        _attachmentFiles,
        _pbgDocumentFiles,
        _customerPoDocumentFiles,
        ...payloadWithoutFiles
      } = payload;

      // Append the JSON payload as a string (backend will parse this)
      formData.append("data", JSON.stringify(payloadWithoutFiles));

      // Append attachment files with proper field name
      attachmentFiles.forEach((file: File, index: number) => {
        formData.append(`attachments`, file, file.name);
      });

      // Append PBG document files with proper field name
      pbgDocumentFiles.forEach((file: File, index: number) => {
        formData.append(`pbg_documents`, file, file.name);
      });

      // Append customer PO document files with proper field name
      customerPoDocumentFiles.forEach((file: File, index: number) => {
        formData.append(`customer_po_documents`, file, file.name);
      });

      // Axios will automatically set Content-Type to multipart/form-data
      // Don't set it manually as it needs to include the boundary
      const response = await Axios.put(
        API_ENDPOINT.SALES_ORDER.PRODUCT.UPDATE(id),
        formData
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Delete product sales order
 */
export const deleteProductSalesOrder = createAsyncThunk(
  "sales_order/deleteProduct",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(`${BASE_URL}/product/${id}/delete/`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch product dropdown
 */
export const fetchProductDropdown = createAsyncThunk(
  "sales_order/productDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.SALES_ORDER.PRODUCT.DROPDOWN
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch product dynamic dropdown
 */
export const fetchProductDynamicDropdown = createAsyncThunk(
  "sales_order/productDynamicDropdown",
  async (fieldName: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.SALES_ORDER.PRODUCT.FILTER_DROPDOWN(fieldName)
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch product sales orders by type
 */
export const fetchSalesOrderProductByType = createAsyncThunk(
  "sales_order/productByType",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
    stage,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    stage: any;
  }) => {
    try {
      const params = buildQueryParams(
        page,
        pageSize,
        search,
        sort,
        order,
        filters,
        stage
      );
      const response = await Axios.get(
        `${BASE_URL}/product/stage/?${params.toString()}`
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return error.message;
    }
  }
);

/* -----------------------------------------------------------
 ⚙️ SERVICE SALES ORDERS
----------------------------------------------------------- */

export const downloadServiceSalesOrderPdf = createAsyncThunk(
  "sales_order/downloadServiceSalesOrderPdf",
  async (
    { id, salesOrderNumber }: { id: string; salesOrderNumber: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.SALES_ORDER.SERVICE.GENERATE_PDF(id),
        {
          responseType: "blob",
        }
      );

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Service_PI_${salesOrderNumber}.pdf`); // Set the file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up the URL object

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to download PDF");
    }
  }
);

export const fetchSalesOrderServiceByType = createAsyncThunk(
  "sales_order/ServiceByType",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    filters,
    stage,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    stage: any;
    type?: string;
  }) => {
    try {
      const params = buildQueryParams(
        page,
        pageSize,
        search,
        sort,
        order,
        filters,
        stage,
        type
      );
      const filterPayload = FilterBuilder(filters ?? []);
      const body = {
        filters: filterPayload,
        sales_order_stage: stage,
        ...(type ? { type } : {}),
      };
      const response = await Axios.post(
        `${API_ENDPOINT.SALES_ORDER.SERVICE.LIST}/?${params.toString()}`,
        body
      );
      // return response.data.data || response.data;
      return {
        data: response.data.data || response.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      return error.message;
    }
  }
);

/**
 * Fetch all service sales orders
 */
export const fetchAllServiceSalesOrders = createAsyncThunk(
  "sales_order/fetchAllServices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT.SALES_ORDER.SERVICE.LIST}`
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Create service sales order
 */
export const createServiceSalesOrder = createAsyncThunk(
  "sales_order/createService",
  async (payload: any, { rejectWithValue }) => {
    try {
      const attachmentFiles = payload._attachmentFiles || [];
      const pbgDocumentFiles = payload._pbgDocumentFiles || [];
      const customerPoDocumentFiles = payload._customerPoDocumentFiles || [];

      const formData = new FormData();
      const {
        _attachmentFiles,
        _pbgDocumentFiles,
        _customerPoDocumentFiles,
        ...payloadWithoutFiles
      } = payload;

      formData.append("data", JSON.stringify(payloadWithoutFiles));

      attachmentFiles.forEach((file: File) => {
        formData.append(`attachments`, file, file.name);
      });

      pbgDocumentFiles.forEach((file: File) => {
        formData.append(`pbg_documents`, file, file.name);
      });

      customerPoDocumentFiles.forEach((file: File) => {
        formData.append(`customer_po_documents`, file, file.name);
      });

      const response = await Axios.post(
        API_ENDPOINT.SALES_ORDER.SERVICE.CREATE,
        formData
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch service sales order by ID
 */
export const fetchServiceSalesOrderById = createAsyncThunk(
  "sales_order/fetchServiceById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.SALES_ORDER.SERVICE.DETAIL(id)
      );
      // console.log(
      //   "response in line 478 fetchServiceSalesOrderById thunk",
      //   response
      // );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update service sales order
 */
export const updateServiceSalesOrder = createAsyncThunk(
  "sales_order/updateService",
  async (
    { id, payload }: { id: string; payload: any },
    { rejectWithValue }
  ) => {
    try {
      const attachmentFiles = payload._attachmentFiles || [];
      const pbgDocumentFiles = payload._pbgDocumentFiles || [];
      const customerPoDocumentFiles = payload._customerPoDocumentFiles || [];

      const formData = new FormData();
      const {
        _attachmentFiles,
        _pbgDocumentFiles,
        _customerPoDocumentFiles,
        ...payloadWithoutFiles
      } = payload;

      formData.append("data", JSON.stringify(payloadWithoutFiles));

      attachmentFiles.forEach((file: File) => {
        formData.append(`attachments`, file, file.name);
      });

      pbgDocumentFiles.forEach((file: File) => {
        formData.append(`pbg_documents`, file, file.name);
      });

      customerPoDocumentFiles.forEach((file: File) => {
        formData.append(`customer_po_documents`, file, file.name);
      });

      const response = await Axios.put(
        API_ENDPOINT.SALES_ORDER.SERVICE.UPDATE(id),
        formData
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Delete service sales order
 */
export const deleteServiceSalesOrder = createAsyncThunk(
  "sales_order/deleteService",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(API_ENDPOINT.SALES_ORDER.SERVICE.DELETE(id));
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch service dropdown
 */
export const fetchServiceDropdown = createAsyncThunk(
  "sales_order/serviceDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.SALES_ORDER.SERVICE.DROPDOWN
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch service dynamic dropdown
 */

/**
 * Fetch service sales orders by type
 */
export const fetchServiceByType = createAsyncThunk(
  "sales_order/serviceByType",
  async (salesOrderType: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${BASE_URL}/service/stage/${salesOrderType}/`
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* -----------------------------------------------------------
 👥 ASSIGNED TO
----------------------------------------------------------- */

export const updateAssignedTo = createAsyncThunk(
  "sales_order/updateAssignedTo",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Axios.put(`${BASE_URL}/assigned-to/`, payload);
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAssignedTo = createAsyncThunk(
  "sales_order/deleteAssignedTo",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`${BASE_URL}/assigned-to/`, {
        data: payload,
      });
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAssignedToBySalesOrder = createAsyncThunk(
  "sales_order/fetchAssignedToBySalesOrder",
  async (
    {
      salesOrderType,
      salesOrderId,
    }: { salesOrderType: string; salesOrderId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(
        `${BASE_URL}/assigned-to/${salesOrderType}/${salesOrderId}/`
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* -----------------------------------------------------------
 📅 FOLLOW UPS
----------------------------------------------------------- */

export const createFollowUp = createAsyncThunk(
  "sales_order/createFollowUp",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `${BASE_URL}/followups/create/`,
        payload
      );
      return response.data.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateFollowUp = createAsyncThunk(
  "sales_order/updateFollowUp",
  async (
    { id, payload }: { id: string; payload: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.put(
        `${BASE_URL}/followups/${id}/update/`,
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteFollowUp = createAsyncThunk(
  "sales_order/deleteFollowUp",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(`${BASE_URL}/followups/${id}/delete/`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFollowUpsBySalesOrder = createAsyncThunk(
  "sales_order/fetchFollowUpsBySalesOrder",
  async (
    {
      salesOrderType,
      salesOrderId,
    }: { salesOrderType: string; salesOrderId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(
        `${BASE_URL}/followups/${salesOrderType}/${salesOrderId}/`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  "sales_order/fetchCategories",
  async (itemtype: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `/sales_order/categories/?item_type=${itemtype}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch categories"
      );
    }
  }
);

// Fetch subcategories by category ID
interface FetchSubcategoriesArgs {
  item_type: "products" | "services" | string; // Use the actual types
  categoryId: string;
}

export const fetchSubcategories = createAsyncThunk(
  "sales_order/fetchSubcategories",
  async (
    { item_type, categoryId }: FetchSubcategoriesArgs,
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.get(
        `/sales_order/subcategories/${categoryId}/?item_type=${item_type}`
      );

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch subcategories"
      );
    }
  }
);

// Fetch makes by category + subcategory
export const fetchMakes = createAsyncThunk(
  "sales_order/fetchMakes",
  async (
    {
      categoryId,
      subcategoryId,
    }: { categoryId: string; subcategoryId: string },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.get(
        `/sales_order/makes/${categoryId}/${subcategoryId}/`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch makes");
    }
  }
);

// Fetch models by category + subcategory + make
export const fetchModels = createAsyncThunk(
  "sales_order/fetchModels",
  async (
    {
      categoryId,
      subcategoryId,
      makeId,
    }: { categoryId: string; subcategoryId: string; makeId: string },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.get(
        `/sales_order/models/${categoryId}/${subcategoryId}/${makeId}/`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch models");
    }
  }
);

export const fetchIssuePackingSalesOrderStatusUpdate = createAsyncThunk(
  "sales_order/fetchIssuePackingSalesOrderStatusUpdate",
  async (
    { issuePackingPayload, id }: { issuePackingPayload: any; id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.patch(
        API_ENDPOINT.SALES_ORDER.PRODUCT.ISSUE_PACKING(id),
        issuePackingPayload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const fetchSalesOrderStatusUpdate = createAsyncThunk(
  "sales_order/fetchSalesOrderStatusUpdate",
  async (
    { id, status }: { id: string; status: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.patch(`${BASE_URL}/product/status/${id}/`, {
        status,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const handleProductSalesOrderStatus = createAsyncThunk(
  "sales_order/handleProductSalesOrderStatus",
  async ({
    id,
    sales_order_stage,
    status,
  }: {
    id: string;
    sales_order_stage: string;
    status?: string;
  }) => {
    const payload = {
      id: id,
      sales_order_stage: sales_order_stage,
      status: status || null,
    };
    const response: any = await Axios.patch(
      API_ENDPOINT.SALES_ORDER.PRODUCT.STATUS_UPDATE(id),
      payload
    );
    return response.data;
  }
);

export const handleServiceSalesOrderStatus = createAsyncThunk(
  "sales_order/handleServiceSalesOrderStatus",
  async ({
    id,
    sales_order_stage,
    status,
  }: {
    id: string;
    sales_order_stage: string;
    status?: string;
  }) => {
    const payload = {
      id: id,
      sales_order_stage: sales_order_stage,
      status: status || null,
    };
    const response: any = await Axios.patch(
      API_ENDPOINT.SALES_ORDER.SERVICE.STATUS_UPDATE(id),
      payload
    );
    return response.data;
  }
);

export const handleRevertProductSalesOrderStatus = createAsyncThunk(
  "sales_order/handleRevertProductSalesOrderStatus",
  async (id: string) => {
    const response: any = await Axios.delete(
      API_ENDPOINT.SALES_ORDER.PRODUCT.REVERT_STATUS_UPDATE(id)
    );
    return response.data;
  }
);

export const handleRevertServiceSalesOrderStatus = createAsyncThunk(
  "sales_order/handleRevertServiceSalesOrderStatus",
  async (id: string) => {
    const response: any = await Axios.delete(
      API_ENDPOINT.SALES_ORDER.SERVICE.REVERT_STATUS_UPDATE(id)
    );
    return response.data;
  }
);

export const fetchProductSalesOrderDropdownForQuotationNumber =
  createAsyncThunk(
    "sales_order/fetchProductSalesOrderDropdownForQuotationNumber",
    async (_, { rejectWithValue }) => {
      try {
        // console.log(
        //   "API_ENDPOINT.QUOTATION.PRODUCT.DROPDOWN_FOR_QUOTATION_NUMBER in line 902",
        //   API_ENDPOINT.QUOTATION.PRODUCT.DROPDOWN_FOR_QUOTATION_NUMBER
        // );
        const response = await Axios.get(
          API_ENDPOINT.QUOTATION.PRODUCT.DROPDOWN_FOR_QUOTATION_NUMBER
        );
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data || error.message);
      }
    }
  );

export const fetchServiceSalesOrderDropdownForQuotationNumber =
  createAsyncThunk(
    "sales_order/fetchServiceSalesOrderDropdownForQuotationNumber",
    async (_, { rejectWithValue }) => {
      try {
        const response = await Axios.get(
          API_ENDPOINT.QUOTATION.SERVICE.DROPDOWN_FOR_QUOTATION_NUMBER
        );
        // console.log("response in line 917", response);
        return response.data?.data || response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data || error.message);
      }
    }
  );

export const fetchProductQuotationById = createAsyncThunk(
  "sales_order/fetchProductQuotationById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.QUOTATION.PRODUCT.DETAIL(id)
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchServiceQuotationById = createAsyncThunk(
  "sales_order/fetchServiceQuotationById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.QUOTATION.SERVICE.DETAIL(id)
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const handleProductGeneratePISalesOrder = createAsyncThunk(
  "sales_order/handleProductGeneratePISalesOrder",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        API_ENDPOINT.SALES_ORDER.PRODUCT.GENERATE_PI(id)
      );
      return response.data ;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const handleServiceGeneratePISalesOrder = createAsyncThunk(
  "sales_order/handleServiceGeneratePISalesOrder",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        API_ENDPOINT.SALES_ORDER.SERVICE.GENERATE_PI(id)
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ----------------------------------------------------
// ✅ EXPORT FUNCTIONS
// ----------------------------------------------------

export const exportSalesOrderProductCSV = async ({
  search,
  filters,
  stage, // sales_order_log | draft | junk
}: {
  search?: string;
  filters?: any;
  stage?: string;
}) => {
  const params = new URLSearchParams();
  if (stage) params.append("stage", stage);
  if (search) params.append("search", search);
  const filterPayload = FilterBuilder(filters ?? []);

  if (!filterPayload.sales_order_stage) {
    filterPayload.sales_order_stage = stage?.toLowerCase(); // "sales_order_log", "draft", or "junk"
  }

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT.SALES_ORDER.PRODUCT.EXPORT}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || `product-sales-order.csv`,
  };
};

export const exportSalesOrderProductTallyCSV = async ({
  search,
  filters,
  stage,
}: {
  search?: string;
  filters?: any;
  stage?: string;
}) => {
  const params = new URLSearchParams();
  if (stage) params.append("stage", stage);
  if (search) params.append("search", search);
  const filterPayload = FilterBuilder(filters ?? []);

  if (!filterPayload.sales_order_stage) {
    filterPayload.sales_order_stage = stage?.toLowerCase();
  }

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT.SALES_ORDER.PRODUCT.EXPORT_TALLY}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    },
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || `product-sales-order-tally.csv`,
  };
};

export const exportSalesOrderServiceCSV = async ({
  search,
  filters,
  stage, // sales_order_log | draft | junk
}: {
  search?: string;
  filters?: any;
  stage?: string;
}) => {
  const params = new URLSearchParams();
  if (stage) params.append("stage", stage);
  if (search) params.append("search", search);
  const filterPayload = FilterBuilder(filters ?? []);

  if (!filterPayload.sales_order_stage) {
    filterPayload.sales_order_stage = stage?.toLowerCase(); // "sales_order_log", "draft", or "junk"
  }

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT.SALES_ORDER.SERVICE.EXPORT}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || `service-sales-order.csv`,
  };
};


export const serviceSalesOrderServiceCompleted = createAsyncThunk(
  "sales_order/serviceSalesOrderServiceCompleted",
  async ({ id, payload }: { id: string; payload: any }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        API_ENDPOINT.SALES_ORDER.SERVICE.SERVICE_COMPLETED(id),
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const serviceSalesOrderServiceItemCompleted = createAsyncThunk(
  "sales_order/serviceSalesOrderServiceItemCompleted",
  async ({ id, payload }: { id: string; payload: any }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(
        API_ENDPOINT.SALES_ORDER.SERVICE.SERVICE_ITEM_COMPLETED(id),
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const handleProductDownloadAcknowledgementSalesOrder = createAsyncThunk(
  "sales_order/handleProductDownloadAcknowledgementSalesOrder",
  async (
    { id, salesOrderNumber }: { id: string; salesOrderNumber: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.SALES_ORDER.PRODUCT.DOWNLOAD_ACKNOWLEDGEMENT_PDF(id),
        {
          responseType: "blob",
        }
      );

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Order_Acknowledgement_${salesOrderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up the URL object

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to download PDF");
    }
  }
);

export const downloadCalibrationEngineerPdf = createAsyncThunk(
  "calibration/downloadCalibrationEngineerPdf",
  async ({ salesOrderServiceId,salesOrderNumber }:{ salesOrderServiceId: string; salesOrderNumber: string;}, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT.SALES_ORDER.SERVICE.CALIBRATION_ENGINEER_PDF(salesOrderServiceId)}`,
        {
          responseType: "blob",
        },
      );

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `GRN_TECH_FORM_${salesOrderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // cleanup

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to download GRN Tech Form PDF",
      );
    }
  },
);

export const downloadRequisitionPdf = createAsyncThunk(
  "calibration/downloadRequisitionPdf",
  async ({ salesOrderServiceId,salesOrderNumber }:{ salesOrderServiceId: string; salesOrderNumber: string;}, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT.SALES_ORDER.SERVICE.REQUISITION_PDF(salesOrderServiceId)}`,
        {
          responseType: "blob",
        },
      );

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `GRN_ADMIN_${salesOrderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // cleanup

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to download GRN Admin PDF",
      );
    }
  },
);